import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Card, 
  CardContent, 
  CardActions,
  TextField, 
  Button, 
  Alert, 
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  LockOutlined,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { Link, useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '../../config';

function ManualLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contextLoaded, setContextLoaded] = useState(false);
  
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  useEffect(() => {
    // Check if auth context is properly loaded
    if (auth && typeof auth.manualLogin === 'function') {
      setContextLoaded(true);
      if (error === 'Authentication system is not available') {
        setError('');
      }
    } else {
      console.warn('Auth context not properly loaded in ManualLogin');
      setContextLoaded(false);
    }
  }, [auth]);
  
  // Safely extract manualLogin function or provide fallback
  const manualLogin = (username, password) => {
    if (auth && typeof auth.manualLogin === 'function') {
      return auth.manualLogin(username, password);
    } else {
      console.error('manualLogin function is not available');
      setError('Authentication system is not available');
      return Promise.resolve(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    if (!contextLoaded) {
      setError('Authentication system is not available. Please try again later.');
      console.error('Auth context not loaded when attempting login');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      console.log('Attempting manual login for user:', username);
      const result = await manualLogin(username, password);

      if (!result?.success) {
        setError('Invalid username or password');
      } else {
        // Use the user returned directly from login — auth.currentUser may not
        // have updated in React state yet (setState is async).
        // Always navigate to executive dashboard '/' regardless of role
        navigate('/');
      }
    } catch (err) {
      setError('Failed to log in. Please try again later.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        py: { xs: 4, md: 8 },
        px: { xs: 2, sm: 3 },
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Card sx={{ 
        width: '100%',
        maxWidth: 450,
        boxShadow: theme.shadows[8],
        borderRadius: 3,
        '&:hover': {
          boxShadow: theme.shadows[12],
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        }
      }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2 
            }}>
              <Box sx={{
                p: 2,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main + '15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <LockOutlined sx={{ 
                  fontSize: { xs: 32, md: 40 },
                  color: 'primary.main'
                }} />
              </Box>
            </Box>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{
                fontSize: { xs: '1.75rem', md: '2.125rem' },
                fontWeight: 700
              }}
            >
              Administrator Login
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              {APP_CONFIG.name}
            </Typography>
          </Box>
          
          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              disabled={loading}
              margin="normal"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <Button
                    onClick={handleTogglePasswordVisibility}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                ),
              }}
              sx={{ mb: 3 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: '1rem', md: '1.125rem' },
                fontWeight: 600,
                borderRadius: 2,
                mb: 3
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  Signing in...
                </Box>
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>
          
          {/* Back to SSO Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={Link}
              to="/"
              variant="text"
              color="primary"
              sx={{
                textTransform: 'none',
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              Back to SSO login
            </Button>
          </Box>
        </CardContent>
        
        {/* Footer */}
        <CardActions sx={{ 
          p: { xs: 2, md: 3 }, 
          pt: 0,
          justifyContent: 'center',
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            &copy; {new Date().getFullYear()} {APP_CONFIG.company}
          </Typography>
        </CardActions>
      </Card>
    </Container>
  );
}

export default ManualLogin;
